import { z } from 'zod';
import { OBJECT_NAMES } from './objects';

const uuid = z.string().uuid();
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

// ---------------------------------------------------------------------------
// Files on records
// ---------------------------------------------------------------------------

export type FileStatus = 'uploading' | 'pending_scan' | 'clean' | 'infected' | 'failed';

export interface FileInfo {
  id: string;
  name: string;
  sizeBytes: number;
  contentType: string;
  status: FileStatus;
  source: 'upload' | 'quote_pdf' | 'export';
  uploadedBy: string;
  uploadedByName: string;
  createdAt: string;
  canDelete: boolean;
}

export const fileUploadSchema = z
  .object({
    name: z.string().trim().min(1).max(255).refine((n) => !/[\\/\0]/.test(n), 'File names can’t contain slashes'),
    sizeBytes: z.number().int().min(1),
    contentType: z.string().trim().max(255).regex(/^[\w.+-]+\/[\w.+-]+$/, 'Unknown file type').default('application/octet-stream'),
    objectName: z.enum(OBJECT_NAMES),
    recordId: uuid,
  })
  .strict();

// ---------------------------------------------------------------------------
// Contacts related to several accounts
// ---------------------------------------------------------------------------

/** Roles on an account-contact relationship. */
export const ACR_ROLES = [
  'Business User', 'Decision Maker', 'Economic Buyer', 'Economic Decision Maker', 'Evaluator',
  'Executive Sponsor', 'Influencer', 'Technical Buyer', 'Other',
] as const;

export interface AccountContactRelation {
  id: string;
  accountId: string;
  accountName: string;
  contactId: string;
  contactName: string;
  isDirect: boolean;
  roles: string[];
  startDate: string | null;
  endDate: string | null;
}

const acrFields = {
  roles: z.array(z.enum(ACR_ROLES)).max(ACR_ROLES.length).default([]),
  startDate: date.nullable().optional(),
  endDate: date.nullable().optional(),
};
const datesInOrder = (v: { startDate?: string | null; endDate?: string | null }) => !v.startDate || !v.endDate || v.endDate >= v.startDate;

export const acrCreateSchema = z
  .object({ contactId: uuid, ...acrFields })
  .strict()
  .refine(datesInOrder, { path: ['endDate'], message: 'End date must be on or after the start date' });

export const acrUpdateSchema = z
  .object({ roles: acrFields.roles.optional(), startDate: acrFields.startDate, endDate: acrFields.endDate })
  .strict()
  .refine(datesInOrder, { path: ['endDate'], message: 'End date must be on or after the start date' });

// ---------------------------------------------------------------------------
// Mass actions from list views
// ---------------------------------------------------------------------------

export const BULK_MAX = 200;

export const bulkActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('delete'), ids: z.array(uuid).min(1).max(BULK_MAX) }).strict(),
  z.object({ action: z.literal('transfer'), ids: z.array(uuid).min(1).max(BULK_MAX), ownerId: uuid }).strict(),
  z.object({ action: z.literal('update'), ids: z.array(uuid).min(1).max(BULK_MAX), values: z.record(z.unknown()) }).strict(),
]);
export type BulkAction = z.infer<typeof bulkActionSchema>;

export interface BulkResult {
  results: { id: string; ok: boolean; error?: string }[];
  succeeded: number;
  failed: number;
}

// ---------------------------------------------------------------------------
// Full org export
// ---------------------------------------------------------------------------

export interface ExportJob {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  fileId: string | null;
  rowCounts: Record<string, number>;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  requestedByName: string;
}

// ---------------------------------------------------------------------------
// Passkeys and single sign-on
// ---------------------------------------------------------------------------

export interface Passkey {
  id: string;
  name: string;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export const passkeyNameSchema = z.string().trim().min(1).max(80);

/** Methods that can complete the second step of sign-in. */
export type MfaMethod = 'totp' | 'passkey' | 'recovery';

/** PEM certificate blocks in a text (an IdP may publish two during a certificate rollover). */
export const pemBlocks = (s: string) => s.match(/-----BEGIN CERTIFICATE-----[\s\S]+?-----END CERTIFICATE-----/g) ?? [];

/**
 * Single sign-on settings. OIDC needs the issuer, client id and secret; SAML 2.0 needs the IdP's
 * entity id, sign-in URL and signing certificate. Omit the secret or certificate to keep the stored one.
 */
export const ssoSettingsSchema = z
  .object({
    protocol: z.enum(['oidc', 'saml']).default('oidc'),
    issuer: z.string().trim().url().max(500).optional(),
    clientId: z.string().trim().min(1).max(255).optional(),
    clientSecret: z.string().min(1).max(1000).optional(),
    samlEntityId: z.string().trim().min(1).max(500).optional(),
    samlSsoUrl: z.string().trim().url().max(1000).optional(),
    samlCertificate: z.string().trim().max(30000).refine((s) => pemBlocks(s).length > 0, 'Paste the certificate in PEM format (-----BEGIN CERTIFICATE-----)').optional(),
    autoProvision: z.boolean().default(false),
    defaultPermissionSetId: uuid.nullable().default(null),
    trustIdpMfa: z.boolean().default(false),
    enforceSso: z.boolean().default(false),
    isActive: z.boolean().default(true),
  })
  .strict()
  .refine((v) => !v.autoProvision || v.defaultPermissionSetId, { path: ['defaultPermissionSetId'], message: 'Choose the permission set new users get' })
  .refine((v) => v.protocol !== 'oidc' || (!!v.issuer && !!v.clientId), { path: ['issuer'], message: 'Enter the issuer URL and client ID' })
  .refine((v) => v.protocol !== 'saml' || (!!v.samlEntityId && !!v.samlSsoUrl), { path: ['samlSsoUrl'], message: 'Enter the identity provider’s entity ID and sign-in URL' });

export interface SsoSettings {
  configured: boolean;
  protocol: 'oidc' | 'saml';
  issuer: string | null;
  clientId: string | null;
  hasSecret: boolean;
  samlEntityId: string | null;
  samlSsoUrl: string | null;
  /** Expiry of each stored IdP certificate (ISO dates). */
  samlCertificates: { subject: string; validTo: string }[];
  autoProvision: boolean;
  defaultPermissionSetId: string | null;
  trustIdpMfa: boolean;
  enforceSso: boolean;
  isActive: boolean;
  /** OIDC redirect URI to register at the provider. */
  redirectUri: string;
  /** SAML service provider values to register at the IdP (set once the provider is saved). */
  saml: { entityId: string; acsUrl: string; metadataUrl: string } | null;
  domains: { domain: string; verified: boolean; txtName: string; txtValue: string }[];
}
export const ssoDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(/^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,63}$/, 'Enter a domain such as example.com');
